import { ClipboardCopyButton } from '@neo/blocks/clipboard-copy-button'
import { ConfirmPopup } from '@neo/blocks/confirm-popup'
import { Button } from '@neo/index'
import { Checkbox } from '@neo/primitives/checkbox'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@neo/primitives/dialog'
import { Input } from '@neo/primitives/input'
import { Label } from '@neo/primitives/label'
import type { ApiResponse } from '@shared/types'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import type { AxiosError } from 'axios'
import { Copy, Eye, EyeOff } from 'lucide-react'
import { useState } from 'react'
import { useFlowStore } from 'v2/pages/flow/store'
import { useAccountNumbersQuery } from 'v2/pages/settings/phone-numbers/queries'
import { resolveBaseUrl } from 'v2/shared/services/create-service'
import { handoffContextService } from 'v2/shared/services/handoff-context-service'
import { getErrorText } from 'v2/shared/utils/api-error-text'
import { conversationQueryKeys } from 'v2/shared/utils/query-key-management'
import { ConvChannels } from 'v2/types/api/conversations'
import type { HandoffApiKey, HandoffConnection } from 'v2/types/api/handoff-context'
import { errorToast, successToast } from 'v2/utils/toasts'
import { ConnectionSteps } from './handoff-connection-steps'
import { integrationInstructions, uploadUrl } from './handoff-context.helpers'

const adminError = (title: string, action: string) => (error: unknown) =>
  errorToast({
    title,
    description:
      (error as AxiosError).response?.status === 403
        ? `Only admins can ${action}.`
        : getErrorText(error as AxiosError<ApiResponse<unknown>>),
  })

const copy = (text: string, what: string) =>
  navigator.clipboard.writeText(text).then(
    () => successToast({ title: `${what} copied` }),
    () => errorToast({ title: `${what} not copied`, description: 'Select and copy it manually.' }),
  )

interface ConnectionDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  connection: HandoffConnection
}

/** The organization's AI agent: the name human specialists see, the API key, the endpoint and the steps. */
export const ConnectionDialog = ({ open, onOpenChange, connection }: ConnectionDialogProps) => {
  const url = uploadUrl(resolveBaseUrl())
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-h-[85vh] max-w-xl grid-cols-[minmax(0,1fr)] overflow-y-auto overflow-x-hidden"
        data-testid="qnr-context-connection"
      >
        <DialogHeader>
          <DialogTitle>Connect your AI agent</DialogTitle>
          <DialogDescription>Shared by every Flow in your organization.</DialogDescription>
        </DialogHeader>
        <NameField connection={connection} />
        {connection.is_admin ? (
          <KeyField />
        ) : (
          <div className="flex flex-col gap-1.5">
            <Label>API key</Label>
            <span className="text-s-400 text-secondary">Only admins can view the API key.</span>
          </div>
        )}
        <Input
          label="Endpoint"
          value={url}
          readOnly
          inputClassName="font-jetbrains pr-10"
          endIcon={
            <ClipboardCopyButton textToCopy={url} aria-label="Copy endpoint" tooltipText="Copy" />
          }
          endIconClassName="pr-3"
        />
        <TransferNumbersField connection={connection} />
        <ConnectionSteps url={url} />
        <DialogFooter className="sm:justify-between">
          <Button
            type="button"
            variant="ghost"
            onClick={() => copy(integrationInstructions(url), 'Setup steps')}
          >
            Copy all steps
          </Button>
          <Button type="button" onClick={() => onOpenChange(false)}>
            Done
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

const useRefreshConnection = () => {
  const queryClient = useQueryClient()
  return () =>
    queryClient.invalidateQueries({ queryKey: conversationQueryKeys().handoffContextConnection })
}

const NameField = ({ connection }: { connection: HandoffConnection }) => {
  const isEditing = useFlowStore((state) => state.isEditing)
  const refresh = useRefreshConnection()
  const [name, setName] = useState<string>()
  const draft = name ?? connection.name
  const rename = useMutation({
    mutationFn: async (value: string) => (await handoffContextService.renameConnection(value)).data,
    onSuccess: () => {
      setName(undefined)
      return refresh()
    },
    onError: adminError('Name not saved', 'change the name'),
  })
  const changed = !!draft.trim() && draft.trim() !== connection.name

  return (
    <div className="flex items-end gap-2">
      <div className="flex-1">
        <Input
          label="Name shown to human specialists"
          value={draft}
          readOnly={!connection.is_admin}
          onChange={(e) => setName(e.target.value)}
        />
      </div>
      {connection.is_admin && changed && (
        <Button
          type="button"
          variant="outline"
          disabled={!isEditing || rename.isPending}
          onClick={() => rename.mutate(draft.trim())}
        >
          Save
        </Button>
      )}
    </div>
  )
}

const MASK = '••••••••••••••••••••••••••••••••'

const KeyField = () => {
  const isEditing = useFlowStore((state) => state.isEditing)
  const refresh = useRefreshConnection()
  // undefined until fetched; null when the stored key predates viewable keys
  const [apiKey, setApiKey] = useState<string | null>()
  const [visible, setVisible] = useState(false)
  const keyLoaded = (key: HandoffApiKey) => {
    setApiKey(key.api_key)
    return refresh()
  }
  const reveal = useMutation({
    mutationFn: async () => (await handoffContextService.revealKey()).data,
    onSuccess: keyLoaded,
    onError: adminError('API key not shown', 'view the API key'),
  })
  const regenerate = useMutation({
    mutationFn: async () => (await handoffContextService.regenerateKey()).data,
    onSuccess: (key) => {
      setVisible(true)
      successToast({ title: 'API key regenerated' })
      return keyLoaded(key)
    },
    onError: adminError('API key not regenerated', 'regenerate the API key'),
  })
  // The key leaves the server only when an admin asks for it.
  const withKey = (use: (key: string) => void) => {
    if (apiKey) return use(apiKey)
    if (apiKey === undefined)
      reveal.mutate(undefined, { onSuccess: (key) => key.api_key && use(key.api_key) })
  }
  const unavailable = reveal.isPending || apiKey === null

  return (
    <div className="flex flex-col gap-1.5">
      <Label htmlFor="handoff-api-key">API key</Label>
      <div className="flex items-center gap-2">
        <div className="min-w-0 flex-1">
          <Input
            id="handoff-api-key"
            value={visible && apiKey ? apiKey : MASK}
            readOnly
            inputClassName="font-jetbrains pr-20"
            data-testid="qnr-context-api-key"
            endIconClassName="pr-1"
            endIcon={
              <div className="flex items-center">
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  disabled={unavailable}
                  aria-label={visible ? 'Hide API key' : 'Show API key'}
                  onClick={() => (visible ? setVisible(false) : withKey(() => setVisible(true)))}
                >
                  {visible ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  disabled={unavailable}
                  aria-label="Copy API key"
                  onClick={() => withKey((key) => copy(key, 'API key'))}
                >
                  <Copy className="size-4" />
                </Button>
              </div>
            }
          />
        </div>
        <ConfirmPopup
          title="Regenerate API key?"
          description="The current key stops working immediately. Update your AI agent with the new key."
          okButtonProps={{
            btnTxt: 'Regenerate',
            variant: 'destructive',
            onClick: () => regenerate.mutate(),
          }}
        >
          <Button type="button" variant="outline" disabled={!isEditing || regenerate.isPending}>
            Regenerate
          </Button>
        </ConfirmPopup>
      </div>
      {apiKey === null && (
        <span className="text-xs-400 text-secondary">
          This key was created before keys could be viewed again. Regenerate it to get a key you can
          copy.
        </span>
      )}
    </div>
  )
}

const e164 = (number: string) => (number.startsWith('+') ? number : `+${number}`)

/** Numbers lent, one per transfer, to AI agents that own the customer's number. */
const TransferNumbersField = ({ connection }: { connection: HandoffConnection }) => {
  const isEditing = useFlowStore((state) => state.isEditing)
  const refresh = useRefreshConnection()
  const { data: numbers } = useAccountNumbersQuery('simple', ConvChannels.CALL)
  const [draft, setDraft] = useState<string[]>()
  const saved = connection.transfer_numbers ?? []
  const selected = draft ?? saved
  const save = useMutation({
    mutationFn: async (value: string[]) =>
      (await handoffContextService.setTransferNumbers(value)).data,
    onSuccess: () => {
      setDraft(undefined)
      return refresh()
    },
    onError: adminError('Transfer numbers not saved', 'change transfer numbers'),
  })
  const changed = [...selected].sort().join() !== [...saved].sort().join()
  const toggle = (number: string, on: boolean) =>
    setDraft(on ? [...selected, number] : selected.filter((n) => n !== number))

  return (
    <div className="flex flex-col gap-1.5" data-testid="qnr-context-transfer-numbers">
      <Label>Transfer numbers</Label>
      <span className="text-xs-400 text-secondary">
        For AI agents that own the customer&apos;s number. Each transfer gets one of these for 2
        minutes. Link them to the Flow that takes AI transfers, and don&apos;t publish them.
      </span>
      {connection.is_admin ? (
        <div className="flex max-h-40 flex-col gap-2 overflow-y-auto pt-1">
          {(numbers ?? []).map(({ number, name_tag }) => (
            <Checkbox
              key={number}
              id={`transfer-number-${e164(number)}`}
              name={`transfer-number-${e164(number)}`}
              label={name_tag ? `${e164(number)} · ${name_tag}` : e164(number)}
              value={selected.includes(e164(number))}
              disabled={!isEditing}
              onChange={(on) => toggle(e164(number), on)}
            />
          ))}
        </div>
      ) : (
        <span className="text-s-400 text-primary">{saved.join(', ') || 'None'}</span>
      )}
      {connection.is_admin && changed && (
        <Button
          type="button"
          variant="outline"
          className="self-start"
          disabled={!isEditing || save.isPending}
          onClick={() => save.mutate(selected)}
        >
          Save transfer numbers
        </Button>
      )}
    </div>
  )
}
